import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "@/lib/api";
import CardAttachments from "@/components/board/CardAttachments";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tag, Calendar as CalendarIcon, Flag, Tags, CheckSquare, MessageSquare,
  Plus, Trash2, X, AlertCircle, Pencil, GripVertical, User as UserIcon, Check,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import RichTextarea from "@/components/RichTextarea";
import Markdown from "@/components/Markdown";
import CardAssignmentsSection from "@/components/board/CardAssignmentsSection";
import { useTranslation } from "react-i18next";

const LABEL_COLORS = ["indigo", "violet", "pink", "emerald", "amber", "sky", "rose", "red"];
const COLOR_BG = {
  indigo: "bg-blue-500", violet: "bg-slate-600", pink: "bg-sky-500",
  emerald: "bg-emerald-500", amber: "bg-amber-500", sky: "bg-sky-500",
  rose: "bg-rose-500", red: "bg-red-500",
};

export default function CardModal({ cardId, boardId, labels, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const dfLocale = i18n.language?.startsWith("en") ? undefined : es;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);

  const { data: card } = useQuery({
    queryKey: ["card", cardId],
    queryFn: async () => (await api.get(`/cards/${cardId}`)).data,
  });

  const { data: comments = [] } = useQuery({
    queryKey: ["comments", cardId],
    queryFn: async () => (await api.get(`/cards/${cardId}/comments`)).data,
  });

  const { data: checklists = [] } = useQuery({
    queryKey: ["checklists", cardId],
    queryFn: async () => (await api.get(`/cards/${cardId}/checklists`)).data,
  });

  useEffect(() => {
    if (card) {
      setTitle(card.title);
      setDescription(card.description || "");
    }
  }, [card]);

  const refreshBoard = () => qc.invalidateQueries({ queryKey: ["board-full", boardId] });

  const updateCard = useMutation({
    mutationFn: async (payload) => (await api.patch(`/cards/${cardId}`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["card", cardId] });
      refreshBoard();
    },
    onError: () => toast.error(t("card.saveFail")),
  });

  const deleteCard = useMutation({
    mutationFn: async () => (await api.delete(`/cards/${cardId}`)).data,
    onSuccess: () => { refreshBoard(); toast.success(t("card.cardDeleted")); onClose(); },
  });

  if (!card) return null;
  const due = card.due_date ? new Date(card.due_date) : null;
  const isOverdue = due && due < new Date();

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-2xl border-white/40 dark:border-white/10 sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl"
        data-testid="card-modal"
      >
        <DialogHeader>
          <DialogTitle className="sr-only">{t("card.srTitle")}</DialogTitle>
          <DialogDescription className="sr-only">{t("card.srDesc")}</DialogDescription>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => { if (title.trim() && title !== card.title) updateCard.mutate({ title: title.trim() }); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid="card-modal-title"
            className="text-xl font-display font-semibold bg-transparent border-0 focus-visible:ring-0 px-0"
          />
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-6 mt-4">
          {/* Left: main content */}
          <div className="space-y-6">
            {/* Labels + Priority + Due summary */}
            <div className="flex flex-wrap gap-2">
              {(card.labels || []).map((id) => {
                const l = labels.find(x => x.label_id === id);
                if (!l) return null;
                return (
                  <span key={id} className={cn("text-xs px-2 py-1 rounded-full text-white", COLOR_BG[l.color] || "bg-blue-500")}>
                    {l.name}
                  </span>
                );
              })}
              {(card.is_urgent || card.is_important) && (() => {
                const u = card.is_urgent, i = card.is_important;
                const q = u && i
                  ? { label: t("card.quadrantDoNow"), cls: "bg-rose-500/15 text-rose-600 dark:text-rose-300" }
                  : !u && i
                  ? { label: t("card.quadrantSchedule"), cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" }
                  : { label: t("card.quadrantDelegate"), cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" };
                return (
                  <span className={cn("text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 font-semibold uppercase tracking-wider", q.cls)}>
                    <Flag className="h-3 w-3" /> {q.label}
                  </span>
                );
              })()}
              {due && (
                <span className={cn(
                  "text-xs px-2 py-1 rounded-full inline-flex items-center gap-1",
                  isOverdue ? "bg-rose-500/15 text-rose-600 dark:text-rose-300" : "bg-slate-500/15 text-slate-600 dark:text-slate-300"
                )}>
                  {isOverdue && <AlertCircle className="h-3 w-3" />} {format(due, "PPP", { locale: dfLocale })}
                </span>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("card.description")}</h3>
              {editingDesc ? (
                <div className="space-y-2">
                  <Textarea
                    value={description} onChange={(e) => setDescription(e.target.value)}
                    autoFocus rows={5}
                    data-testid="card-description-textarea"
                    className="bg-white/60 dark:bg-slate-900/40"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="card-description-save"
                      onClick={() => { updateCard.mutate({ description }); setEditingDesc(false); }}
                    >{t("common.save")}</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingDesc(false); setDescription(card.description || ""); }}>{t("common.cancel")}</Button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setEditingDesc(true)} data-testid="card-description-edit"
                  className="text-left w-full text-sm whitespace-pre-wrap p-3 rounded-xl bg-white/40 dark:bg-slate-900/40 hover:bg-white/60 dark:hover:bg-slate-900/60 transition border border-white/30 dark:border-white/5 min-h-[3rem]"
                >
                  {description || <span className="text-muted-foreground">{t("card.addDescription")}</span>}
                </button>
              )}
            </div>

            {/* Checklists */}
            <Checklists cardId={cardId} boardId={boardId} checklists={checklists} qc={qc} />

            {/* Checklist progress by user */}
            <ChecklistProgress cardId={cardId} />

            {/* Attachments */}
            <CardAttachments cardId={cardId} />

            {/* Comments */}
            <Comments cardId={cardId} comments={comments} qc={qc} user={user} />
          </div>

          {/* Right: actions */}
          <div className="space-y-2">
            <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("card.add")}</h3>

            <CardAssignmentsSection cardId={cardId} boardId={boardId} card={card} qc={qc} />

            <LabelsPopover boardId={boardId} card={card} labels={labels} qc={qc} />

            <DatePopover
              card={card}
              field="start_date"
              label={t("card.startDate")}
              onChange={(d) => updateCard.mutate({ start_date: d })}
            />
            <DatePopover
              card={card}
              field="due_date"
              label={t("card.dueDate")}
              onChange={(d) => updateCard.mutate({ due_date: d })}
            />

            <PriorityToggles card={card} onChange={(patch) => updateCard.mutate(patch)} />

            <AddChecklist cardId={cardId} qc={qc} />

            <div className="pt-3 border-t border-white/30 dark:border-white/10">
              <button
                data-testid="card-delete-button"
                onClick={() => { if (window.confirm(t("card.deleteCardConfirm"))) deleteCard.mutate(); }}
                className="w-full text-left px-3 py-2 rounded-lg text-sm text-rose-500 hover:bg-rose-500/10 inline-flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" /> {t("card.deleteCard")}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- subcomponents ---------- */

function ActionButton({ icon: Icon, label, ...props }) {
  return (
    <button
      {...props}
      className="w-full text-left px-3 py-2 rounded-lg text-sm bg-white/40 dark:bg-slate-900/40 hover:bg-white/60 dark:hover:bg-slate-900/60 inline-flex items-center gap-2 transition"
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function LabelsPopover({ boardId, card, labels, qc }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [color, setColor] = useState("indigo");

  const toggle = async (label_id) => {
    const current = card.labels || [];
    const next = current.includes(label_id) ? current.filter(x => x !== label_id) : [...current, label_id];
    await api.patch(`/cards/${card.card_id}`, { labels: next });
    qc.invalidateQueries({ queryKey: ["card", card.card_id] });
    qc.invalidateQueries({ queryKey: ["board-full", boardId] });
  };
  const create = async () => {
    if (!name.trim()) return;
    await api.post(`/boards/${boardId}/labels`, { name: name.trim(), color });
    setName("");
    qc.invalidateQueries({ queryKey: ["board-full", boardId] });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="card-action-labels"
          className="w-full text-left px-3 py-2 rounded-lg text-sm bg-white/40 dark:bg-slate-900/40 hover:bg-white/60 dark:hover:bg-slate-900/60 inline-flex items-center gap-2 transition"
        >
          <Tags className="h-4 w-4" /> {t("card.labels")}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 glass-panel rounded-2xl">
        <div className="space-y-1.5">
          {labels.map((l) => {
            const on = (card.labels || []).includes(l.label_id);
            return (
              <button
                key={l.label_id}
                onClick={() => toggle(l.label_id)}
                data-testid={`card-toggle-label-${l.label_id}`}
                className={cn("w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-white/40 dark:hover:bg-white/10",
                  on && "bg-blue-500/10")}
              >
                <span className={cn("h-3 w-3 rounded-full", COLOR_BG[l.color])} />
                <span className="flex-1 text-left">{l.name}</span>
                {on && <X className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            );
          })}
          <div className="pt-2 border-t border-white/30 dark:border-white/10 space-y-2">
            <Input
              placeholder={t("card.newLabel")}
              value={name} onChange={(e) => setName(e.target.value)}
              className="bg-white/60 dark:bg-slate-900/40"
              data-testid="new-label-name"
            />
            <div className="flex gap-1.5 flex-wrap">
              {LABEL_COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} data-testid={`new-label-color-${c}`}
                  className={cn("h-6 w-6 rounded-full", COLOR_BG[c], color === c && "ring-2 ring-offset-2 ring-offset-transparent ring-blue-500")} />
              ))}
            </div>
            <Button size="sm" onClick={create} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-label-create">
              <Plus className="h-4 w-4 mr-1" /> {t("card.createLabel")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DatePopover({ card, field = "due_date", label, onChange }) {
  const { t, i18n } = useTranslation();
  const dfLocale = i18n.language?.startsWith("en") ? undefined : es;
  const val = card[field] ? new Date(card[field]) : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`card-action-${field === "start_date" ? "start" : "due"}`}
          className="w-full text-left px-3 py-2 rounded-lg text-sm bg-white/40 dark:bg-slate-900/40 hover:bg-white/60 dark:hover:bg-slate-900/60 inline-flex items-center gap-2 transition"
        >
          <CalendarIcon className="h-4 w-4" /> {val ? format(val, "PPP", { locale: dfLocale }) : label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="glass-panel rounded-2xl p-2">
        <Calendar
          mode="single"
          selected={val || undefined}
          onSelect={(d) => onChange(d ? d.toISOString() : null)}
        />
        <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => onChange(null)} data-testid={`card-clear-${field === "start_date" ? "start" : "due"}`}>
          <X className="h-3.5 w-3.5 mr-1" /> {t("card.clearDate")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function PriorityToggles({ card, onChange }) {
  const { t } = useTranslation();
  const u = !!card.is_urgent;
  const i = !!card.is_important;
  const QUADRANT = (() => {
    if (u && i) return { label: t("card.quadrantDoNow"), cls: "bg-rose-500/15 text-rose-600 dark:text-rose-300" };
    if (!u && i) return { label: t("card.quadrantSchedule"),  cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" };
    if (u && !i) return { label: t("card.quadrantDelegate"),    cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" };
    return { label: t("card.quadrantPostpone"), cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" };
  })();

  return (
    <div className="rounded-lg bg-white/40 dark:bg-slate-900/40 p-3 space-y-2 border border-white/30 dark:border-white/5" data-testid="card-priority-toggles">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Flag className="h-4 w-4" />
        {t("card.priority")}
        <span className={cn("ml-auto text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider", QUADRANT.cls)}>{QUADRANT.label}</span>
      </div>
      <label className="flex items-center justify-between text-sm cursor-pointer">
        <span>{t("card.urgent")}</span>
        <input
          type="checkbox"
          checked={u}
          onChange={(e) => onChange({ is_urgent: e.target.checked })}
          data-testid="card-toggle-urgent"
          className="h-4 w-4 rounded accent-blue-600"
        />
      </label>
      <label className="flex items-center justify-between text-sm cursor-pointer">
        <span>{t("card.important")}</span>
        <input
          type="checkbox"
          checked={i}
          onChange={(e) => onChange({ is_important: e.target.checked })}
          data-testid="card-toggle-important"
          className="h-4 w-4 rounded accent-blue-600"
        />
      </label>
    </div>
  );
}

function AddChecklist({ cardId, qc }) {
  const { t } = useTranslation();
  return (
    <ActionButton
      icon={CheckSquare} label={t("card.checklist")}
      data-testid="card-action-add-checklist"
      onClick={async () => {
        const title = window.prompt(t("card.checklistTitle"), t("card.checklistDefault"));
        if (!title) return;
        await api.post(`/cards/${cardId}/checklists`, { title });
        qc.invalidateQueries({ queryKey: ["checklists", cardId] });
      }}
    />
  );
}

function Checklists({ cardId, boardId, checklists, qc }) {
  const { t } = useTranslation();

  const onDragEnd = async (result, items, checklistId) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    // Compute new position: midway between neighbours (exclude the moved item).
    const others = items.filter((_, i) => i !== from);
    const prev = others[to - 1];
    const next = others[to];
    let newPos;
    if (prev && next) newPos = (prev.position + next.position) / 2;
    else if (next) newPos = next.position - 1000;
    else if (prev) newPos = prev.position + 1000;
    else newPos = 1000;
    const moved = items[from];
    // Optimistic update
    qc.setQueryData(["checklists", cardId], (prevData) => {
      if (!prevData) return prevData;
      return prevData.map(cl => {
        if (cl.checklist_id !== checklistId) return cl;
        const reordered = Array.from(cl.items);
        reordered.splice(from, 1);
        reordered.splice(to, 0, { ...moved, position: newPos });
        return { ...cl, items: reordered };
      });
    });
    try {
      await api.patch(`/checklist-items/${moved.item_id}`, { position: newPos });
    } catch {
      toast.error(t("card.itemReorderError", "No se pudo reordenar"));
      qc.invalidateQueries({ queryKey: ["checklists", cardId] });
    }
  };

  if (!checklists || checklists.length === 0) return null;
  return (
    <div className="space-y-4">
      {checklists.map((cl) => {
        const total = cl.items.length;
        const done = cl.items.filter(i => i.is_done).length;
        const pct = total ? (done / total) * 100 : 0;
        return (
          <div key={cl.checklist_id} className="space-y-2" data-testid={`checklist-${cl.checklist_id}`}>
            <div className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              <span className="font-semibold">{cl.title}</span>
              <span className="text-xs text-muted-foreground ml-auto">{done}/{total}</span>
              <button
                onClick={async () => {
                  if (window.confirm(t("card.checklistDeleteConfirm"))) {
                    await api.delete(`/checklists/${cl.checklist_id}`);
                    qc.invalidateQueries({ queryKey: ["checklists", cardId] });
                  }
                }}
                className="h-6 w-6 grid place-items-center rounded-md hover:bg-rose-500/10 text-rose-500"
                data-testid={`checklist-delete-${cl.checklist_id}`}
              ><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <div className="h-1.5 bg-white/40 dark:bg-slate-800/60 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <DragDropContext onDragEnd={(r) => onDragEnd(r, cl.items, cl.checklist_id)}>
              <Droppable droppableId={cl.checklist_id} type="CHECKLIST_ITEM">
                {(prov) => (
                  <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-1.5">
                    {cl.items.map((it, idx) => (
                      <Draggable key={it.item_id} draggableId={it.item_id} index={idx}>
                        {(dp, snap) => (
                          <div
                            ref={dp.innerRef}
                            {...dp.draggableProps}
                            style={dp.draggableProps.style}
                            className={cn(snap.isDragging && "opacity-90 shadow-lg")}
                          >
                            <ChecklistItemRow
                              item={it}
                              cardId={cardId}
                              boardId={boardId}
                              qc={qc}
                              dragHandleProps={dp.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {prov.placeholder}
                    <AddChecklistItem checklistId={cl.checklist_id} onAdded={() => qc.invalidateQueries({ queryKey: ["checklists", cardId] })} />
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        );
      })}
    </div>
  );
}

function ChecklistItemRow({ item, cardId, boardId, qc, dragHandleProps }) {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [dateOpen, setDateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const dfLocale = i18n.language?.startsWith("en") ? undefined : es;

  const { data: boardDetails } = useQuery({
    queryKey: ["board-details", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/details`)).data,
    staleTime: 60_000,
    enabled: !!boardId,
  });

  const members = (() => {
    const list = [];
    if (boardDetails?.author) list.push({ ...boardDetails.author, is_creator: true });
    for (const m of boardDetails?.members || []) {
      if (m.status !== "active") continue;
      if (list.find((x) => x.user_id === m.user_id)) continue;
      list.push({ ...m, is_creator: false });
    }
    return list;
  })();

  const assignee = item.assigned_to ? members.find((m) => m.user_id === item.assigned_to) : null;

  const patch = async (payload) => {
    try {
      await api.patch(`/checklist-items/${item.item_id}`, payload);
      qc.invalidateQueries({ queryKey: ["checklists", cardId] });
      qc.invalidateQueries({ queryKey: ["checklist-progress", cardId] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("card.itemUpdateError", "No se pudo actualizar"));
    }
  };

  const saveText = async () => {
    const trimmed = text.trim();
    if (!trimmed) { toast.error(t("card.itemEmptyError", "El texto no puede estar vacío")); return; }
    if (trimmed === item.text) { setIsEditing(false); return; }
    await patch({ text: trimmed });
    setIsEditing(false);
  };
  const cancelEdit = () => { setText(item.text); setIsEditing(false); };
  const remove = async () => {
    try {
      await api.delete(`/checklist-items/${item.item_id}`);
      qc.invalidateQueries({ queryKey: ["checklists", cardId] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("card.itemDeleteError", "No se pudo eliminar"));
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-white/60 dark:bg-white/5"
           data-testid={`item-${item.item_id}`}>
        <input type="checkbox" checked={item.is_done} disabled
               className="h-4 w-4 rounded accent-blue-600 opacity-60" />
        <Input
          autoFocus value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); saveText(); }
            if (e.key === "Escape") cancelEdit();
          }}
          className="h-8 flex-1 bg-white/60 dark:bg-slate-900/40"
          data-testid={`item-rename-input-${item.item_id}`}
        />
        <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={saveText} data-testid={`item-rename-save-${item.item_id}`}>
          {t("common.save", "Guardar")}
        </Button>
        <Button size="sm" variant="ghost" className="h-8"
                onClick={cancelEdit} data-testid={`item-rename-cancel-${item.item_id}`}>
          {t("common.cancel", "Cancelar")}
        </Button>
      </div>
    );
  }

  // Due date styling: overdue = rose, upcoming (≤2d) = amber, else = slate
  let dueTone = "slate";
  if (item.due_date && !item.is_done) {
    const dueMs = new Date(item.due_date).getTime();
    const now = Date.now();
    if (dueMs < now) dueTone = "rose";
    else if (dueMs - now < 2 * 24 * 3600 * 1000) dueTone = "amber";
  }
  const dueCls = {
    slate: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
    amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  }[dueTone];

  return (
    <div className="group flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/40 dark:hover:bg-white/10"
         data-testid={`item-${item.item_id}`}>
      <button
        {...dragHandleProps}
        className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing text-muted-foreground -ml-1 h-5 w-4 grid place-items-center transition-opacity"
        aria-label={t("card.itemDrag", "Arrastrar")}
        data-testid={`item-drag-${item.item_id}`}
      ><GripVertical className="h-3.5 w-3.5" /></button>
      <input
        type="checkbox" checked={item.is_done}
        onChange={(e) => patch({ is_done: e.target.checked })}
        className="h-4 w-4 rounded accent-blue-600 cursor-pointer"
        data-testid={`item-check-${item.item_id}`}
      />
      <span className={cn("text-sm flex-1 select-none", item.is_done && "line-through text-muted-foreground")}>
        {item.text}
      </span>

      {item.due_date && (
        <span className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium", dueCls)}
              data-testid={`item-due-badge-${item.item_id}`}>
          <CalendarIcon className="h-3 w-3" />
          {format(new Date(item.due_date), "d MMM", { locale: dfLocale })}
        </span>
      )}
      {assignee && (
        <Avatar className="h-5 w-5" data-testid={`item-assignee-avatar-${item.item_id}`}>
          {assignee.picture && <AvatarImage src={assignee.picture} alt={assignee.name} />}
          <AvatarFallback className="text-[9px]">{initials(assignee.name)}</AvatarFallback>
        </Avatar>
      )}

      {/* Due date picker (opened from dropdown) */}
      <Popover open={dateOpen} onOpenChange={setDateOpen}>
        <PopoverTrigger asChild><span className="sr-only" /></PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={item.due_date ? new Date(item.due_date) : undefined}
            onSelect={(d) => {
              if (d) {
                patch({ due_date: new Date(d.setHours(12, 0, 0, 0)).toISOString() });
                setDateOpen(false);
              }
            }}
            locale={dfLocale}
          />
          {item.due_date && (
            <div className="border-t border-slate-200 dark:border-white/10 p-2 flex justify-end">
              <Button
                variant="ghost" size="sm" className="text-rose-500 hover:text-rose-600"
                onClick={() => { patch({ due_date: "" }); setDateOpen(false); }}
                data-testid={`item-due-clear-${item.item_id}`}
              >{t("card.clearDate", "Quitar fecha")}</Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Assignee picker (opened from dropdown) */}
      <Popover open={assignOpen} onOpenChange={setAssignOpen}>
        <PopoverTrigger asChild><span className="sr-only" /></PopoverTrigger>
        <PopoverContent className="w-56 p-1" align="end">
          <div className="max-h-64 overflow-y-auto">
            {members.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground text-center">
                {t("card.itemNoMembers", "Sin miembros disponibles")}
              </div>
            )}
            {members.map((m) => (
              <button
                key={m.user_id}
                onClick={() => { patch({ assigned_to: m.user_id }); setAssignOpen(false); }}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-slate-100 dark:hover:bg-white/5",
                  item.assigned_to === m.user_id && "bg-blue-500/10"
                )}
                data-testid={`item-assign-option-${item.item_id}-${m.user_id}`}
              >
                <Avatar className="h-6 w-6">
                  {m.picture && <AvatarImage src={m.picture} alt={m.name} />}
                  <AvatarFallback className="text-[10px]">{initials(m.name)}</AvatarFallback>
                </Avatar>
                <span className="truncate">{m.name}</span>
                {item.assigned_to === m.user_id && <Check className="h-3.5 w-3.5 ml-auto text-blue-500" />}
              </button>
            ))}
          </div>
          {item.assigned_to && (
            <div className="border-t border-slate-200 dark:border-white/10 p-1">
              <button
                onClick={() => { patch({ assigned_to: "" }); setAssignOpen(false); }}
                className="w-full text-left px-2 py-1.5 rounded-md text-sm text-rose-500 hover:bg-rose-500/10"
                data-testid={`item-assign-clear-${item.item_id}`}
              >{t("card.itemUnassign", "Quitar responsable")}</button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="h-6 w-6 grid place-items-center rounded-md opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100 hover:bg-white/60 dark:hover:bg-white/10 text-muted-foreground hover:text-foreground transition-opacity"
            aria-label={t("card.itemActions", "Acciones del elemento")}
            data-testid={`item-menu-${item.item_id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onSelect={() => { setText(item.text); setIsEditing(true); }}
                            data-testid={`item-rename-${item.item_id}`}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            {t("card.itemRename", "Renombrar")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setDateOpen(true); }}
                            data-testid={`item-set-date-${item.item_id}`}>
            <CalendarIcon className="h-3.5 w-3.5 mr-2" />
            {item.due_date ? t("card.itemChangeDate", "Cambiar fecha") : t("card.itemSetDate", "Fecha límite")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setAssignOpen(true); }}
                            data-testid={`item-set-assignee-${item.item_id}`}>
            <UserIcon className="h-3.5 w-3.5 mr-2" />
            {assignee ? t("card.itemChangeAssignee", "Cambiar responsable") : t("card.itemSetAssignee", "Asignar responsable")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={remove} className="text-rose-500 focus:text-rose-600"
                            data-testid={`item-delete-${item.item_id}`}>
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            {t("card.itemDelete", "Eliminar")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function initials(name = "") {
  return name.split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

function ChecklistProgress({ cardId }) {
  const { t } = useTranslation();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["checklist-progress", cardId],
    queryFn: async () => (await api.get(`/cards/${cardId}/checklist-progress`)).data,
    refetchInterval: 15_000,  // keep counters fresh as items get toggled
  });
  if (isLoading || rows.length === 0) return null;
  return (
    <div className="rounded-lg bg-white/40 dark:bg-slate-900/40 p-3 border border-white/30 dark:border-white/5 space-y-2"
         data-testid="checklist-progress">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
        <UserIcon className="h-3.5 w-3.5" /> {t("card.progressByUser", "Pendientes por responsable")}
      </div>
      <ul className="space-y-1.5">
        {rows.map(r => {
          const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
          return (
            <li key={r.user_id}
                className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-white/40 dark:hover:bg-white/5"
                data-testid={`progress-${r.user_id}`}>
              <Avatar className="h-7 w-7 shrink-0">
                {r.picture && <AvatarImage src={r.picture} alt={r.name} />}
                <AvatarFallback className="text-[10px]">{initials(r.name || "?")}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{r.name}</span>
                  <span className="text-xs text-muted-foreground ml-auto tabular-nums">
                    {r.done}/{r.total}
                  </span>
                </div>
                <div className="h-1 mt-1 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r.pending > 0 && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-blue-500/15 text-blue-600 dark:text-blue-300"
                        data-testid={`progress-pending-${r.user_id}`}>
                    {r.pending} {t("card.progressPending", "pendientes")}
                  </span>
                )}
                {r.overdue > 0 && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-rose-500/15 text-rose-600 dark:text-rose-300"
                        data-testid={`progress-overdue-${r.user_id}`}>
                    {r.overdue} {t("card.progressOverdue", "vencidos")}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AddChecklistItem({ checklistId, onAdded }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 inline-flex items-center gap-1"
        data-testid={`add-item-trigger-${checklistId}`}
      ><Plus className="h-3.5 w-3.5" /> {t("card.addItem")}</button>
    );
  }
  return (
    <div className="flex gap-2">
      <Input
        autoFocus value={text} onChange={(e) => setText(e.target.value)}
        placeholder={t("card.addItem")} className="bg-white/60 dark:bg-slate-900/40 h-8"
        data-testid={`add-item-input-${checklistId}`}
        onKeyDown={async (e) => {
          if (e.key === "Enter" && text.trim()) {
            await api.post(`/checklists/${checklistId}/items`, { text: text.trim() });
            setText(""); onAdded();
          }
          if (e.key === "Escape") { setEditing(false); setText(""); }
        }}
      />
      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
        data-testid={`add-item-submit-${checklistId}`}
        onClick={async () => {
          if (!text.trim()) return;
          await api.post(`/checklists/${checklistId}/items`, { text: text.trim() });
          setText(""); onAdded();
        }}>{t("card.add")}</Button>
      <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(""); }}><X className="h-4 w-4" /></Button>
    </div>
  );
}

function Comments({ cardId, comments, qc, user }) {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const send = async () => {
    if (!body.trim()) return;
    await api.post(`/cards/${cardId}/comments`, { body });
    setBody("");
    qc.invalidateQueries({ queryKey: ["comments", cardId] });
  };
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
        <MessageSquare className="h-4 w-4" /> {t("card.comments")}
      </h3>
      <div className="flex items-start gap-3 mb-4">
        <div className="h-8 w-8 rounded-full bg-blue-500/20 grid place-items-center text-xs font-semibold flex-none">{user?.name?.[0]?.toUpperCase() || "U"}</div>
        <div className="flex-1">
          <RichTextarea
            value={body}
            onChange={setBody}
            placeholder={t("card.commentPh")}
            rows={2}
            data-testid="comment-input"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" onClick={send} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="comment-submit" disabled={!body.trim()}>{t("card.commentBtn")}</Button>
            {body && (
              <span className="text-xs text-muted-foreground">{t("card.supportsMarkdown")}</span>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {comments.map((c) => (
          <CommentItem key={c.comment_id} comment={c} cardId={cardId} qc={qc} currentUser={user} />
        ))}
        {comments.length === 0 && <p className="text-sm text-muted-foreground">{t("card.noComments")}</p>}
      </div>
    </div>
  );
}

function CommentItem({ comment: c, cardId, qc, currentUser }) {
  const { t, i18n } = useTranslation();
  const dfLocale = i18n.language?.startsWith("en") ? undefined : es;
  const isMine = c.author_id === currentUser?.user_id;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);

  useEffect(() => { setDraft(c.body); }, [c.body]);

  const saveEdit = async () => {
    if (!draft.trim()) return;
    try {
      await api.patch(`/comments/${c.comment_id}`, { body: draft.trim() });
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["comments", cardId] });
      toast.success(t("card.commentUpdated"));
    } catch {
      toast.error(t("card.commentSaveFail"));
    }
  };

  const remove = async () => {
    if (!window.confirm(t("card.deleteCommentConfirm"))) return;
    try {
      await api.delete(`/comments/${c.comment_id}`);
      qc.invalidateQueries({ queryKey: ["comments", cardId] });
    } catch {
      toast.error(t("card.commentDeleteFail"));
    }
  };

  return (
    <div className="flex items-start gap-3 group" data-testid={`comment-${c.comment_id}`}>
      <div className="h-8 w-8 rounded-full bg-blue-500/20 grid place-items-center text-xs font-semibold flex-none">
        {c.author?.name?.[0]?.toUpperCase() || "U"}
      </div>
      <div className="flex-1 glass-card rounded-xl p-3 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium text-sm truncate">{c.author?.name || t("card.userFallback")}</span>
            <span className="text-xs text-muted-foreground">
              {format(new Date(c.created_at), "Pp", { locale: dfLocale })}
              {c.updated_at && c.updated_at !== c.created_at && (
                <span className="ml-1 italic">· {t("card.edited")}</span>
              )}
            </span>
          </div>
          {isMine && !editing && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
              <button
                onClick={() => setEditing(true)}
                data-testid={`comment-edit-${c.comment_id}`}
                aria-label={t("card.editCommentAria")}
                className="h-7 w-7 grid place-items-center rounded-md hover:bg-white/50 dark:hover:bg-white/10"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={remove}
                data-testid={`comment-delete-${c.comment_id}`}
                aria-label={t("card.deleteCommentAria")}
                className="h-7 w-7 grid place-items-center rounded-md text-rose-500 hover:bg-rose-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="mt-2 space-y-2">
            <RichTextarea
              value={draft}
              onChange={setDraft}
              rows={3}
              autoFocus
              data-testid={`comment-edit-input-${c.comment_id}`}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={saveEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid={`comment-edit-save-${c.comment_id}`}
                disabled={!draft.trim() || draft.trim() === c.body.trim()}
              >{t("common.save")}</Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setEditing(false); setDraft(c.body); }}
                data-testid={`comment-edit-cancel-${c.comment_id}`}
              >{t("common.cancel")}</Button>
            </div>
          </div>
        ) : (
          <div className="mt-1">
            <Markdown>{c.body}</Markdown>
          </div>
        )}
      </div>
    </div>
  );
}
